import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Menu,
  Grid,
  MenuItem,
  Pagination,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import CreateRecipeModal from "../../components/Modals/CreateRecipeModal";
import RecipeList from "./components/RecipeList";
import SpecSpinner from "../../components/SpecSpinner";
import { useMutation, useQueryClient } from "react-query";
import inventoryClient from "../../clients/inventoryClient";
import { useNavigate, useParams } from "react-router-dom";
import useEstablishmentTags from "../../hooks/useEstablishmentTags";
import Store, { activeRouteKeys } from "../../Store/Store";
import useRecipesPaginated from "../../hooks/useRecipesPaginated";
import FilterListIcon from "@mui/icons-material/FilterList";
import useRecipePaginatedSearch from "../../hooks/useRecipePaginatedSearch";
import useRecipeIngredientPaginatedSearch from "../../hooks/useRecipeIngredientPaginatedSearch";
import SearchByToggle from "./components/SearchByToggle";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import { cloneDeep, debounce } from "lodash";
import useEstablishmentSubscription from "../../hooks/useEstablishmentSubscription";
import useEstablishmentRateLimits from "../../hooks/useEstablishmentRateLimits";
import inventoryObjects from "../../clients/inventoryObjects";
import useEstablishmentRecipes from "../../hooks/useEstablishmentRecipes";
import TagPill from "../../components/TagPill";
import TagsModal from "../../components/Modals/TagsModal";
import UpgradeAccountModal from "../../components/Modals/UpgradeAccountModal";
import { createStripeCheckoutSession } from "../../clients/establishmentsClient";
import useMyMemberships from "../../hooks/useMyMemberships";
import CreateRecipeGuide from "./components/CreateRecipeGuide";

const Recipes = () => {
  const { establishmentid } = useParams();
  const queryClient = useQueryClient();
  const [creatingRecipeModalOpen, toggleCreatingRecipeModalOpen] =
    useState(false);
  const [upgradeAccountModalOpen, setUpgradeAccountModalOpen] = useState(false);
  const [isLoadingSession, toggleLoadingSession] = useState(false);

  const [sortMenuAnchor, setSortMenuAnchor] = useState(null);
  const [localSearchText, setLocalSearchText] = useState("");
  const store = useContext(Store);
  const [tagsModalOpen, toggleTagsModalOpen] = useState(false);
  const [activeTagFilters, setActiveTagFilters] = useState([]);
  const disableBaseData = store?.recipeSearchText ? true : false;
  const isIngredientSearch =
    store?.recipeSearchType === inventoryObjects.recipeSearchTypes.ingredient;
  const { data: paginatedRecipeData, isLoading: isLoadingPaginatedRecipes } =
    useRecipesPaginated(
      establishmentid,
      store?.recipeSelectedPage,
      store?.recipeFilters,
      disableBaseData
    );
  // only the search mode that is toggled on fetches - the other stays idle
  const { data: nameSearchData, isLoading: isLoadingNameSearchData } =
    useRecipePaginatedSearch(
      establishmentid,
      store?.recipeSearchText,
      store?.recipeSelectedPage,
      store?.recipeFilters,
      isIngredientSearch
    );
  const {
    data: ingredientSearchData,
    isLoading: isLoadingIngredientSearchData,
  } = useRecipeIngredientPaginatedSearch(
    establishmentid,
    store?.recipeSearchText,
    store?.recipeSelectedPage,
    store?.recipeFilters,
    !isIngredientSearch
  );
  const paginatedSearchData = isIngredientSearch
    ? ingredientSearchData
    : nameSearchData;
  const isLoadingPaginatedSearchData = isIngredientSearch
    ? isLoadingIngredientSearchData
    : isLoadingNameSearchData;
  const { isLoading: isLoadingTags, data: recipeTagData } =
    useEstablishmentTags(establishmentid);
  const { data: subscriptionData, isLoading: isLoadingSubscription } =
    useEstablishmentSubscription(establishmentid);
  const { data: rateLimitData, isLoading: isLoadingRateLimits } =
    useEstablishmentRateLimits(
      establishmentid,
      subscriptionData?.isActive === false ? true : false
    );
  const { data: memberships, isLoading: isLoadingMemberships } =
    useMyMemberships();

  // we want to remove this when everything moves to using the paginated view. The reason its here is to ensure the data is loaded when we update
  // its cache as a result of adding or removing recipes.
  const { isLoading: isLoadingFullLibrary } =
    useEstablishmentRecipes(establishmentid);
  const navigate = useNavigate();
  const recipeRef = useRef();
  useEffect(() => {
    setLocalSearchText(store?.recipeSearchText);
    store.updateActiveRoute(
      activeRouteKeys.recipes,
      `spec/${establishmentid}/recipes`
    );

    if (localStorage.getItem("showInactiveRecipes") !== null) {
      let sortObj = cloneDeep(store.recipeFilters);
      sortObj.showInactive = localStorage.getItem("showInactiveRecipes");
      store.setRecipeFilters(sortObj);
    }
  }, []);

  const addRecipeMutation = useMutation(inventoryClient.addRecipe, {
    onSuccess: (result) => {
      queryClient.invalidateQueries({
        queryKey: [
          "recipes-paginated",
          {
            establishmentId: establishmentid,
          },
        ],
      });
      queryClient.refetchQueries({
        queryKey: [
          "recipes-paginated",
          {
            establishmentId: establishmentid,
          },
        ],
        type: "all",
      });
      queryClient.invalidateQueries({
        queryKey: [
          "rate-limits",
          {
            establishmentId: store.currentEstablishment?.id,
          },
        ],
        type: "all",
      });
      queryClient.refetchQueries({
        queryKey: [
          "rate-limits",
          {
            establishmentId: store.currentEstablishment?.id,
          },
        ],
        type: "all",
      });

      queryClient.setQueryData(
        ["recipes", { establishmentId: establishmentid }],
        (prev) => {
          const newRecipes = [...prev.recipes, result.recipe];

          return { recipes: newRecipes };
        }
      );
      navigate(result?.recipe?.id);
    },
  });

  const handleCreateTagCallback = () => {
    queryClient.invalidateQueries({
      queryKey: ["recipe-tags", { establishmentId: establishmentid }],
    });
    queryClient.refetchQueries({
      queryKey: ["recipe-tags", { establishmentId: establishmentid }],
    });
  };

  const handlePageSet = (_, page) => {
    store.setRecipeSelectedPage(page);
    recipeRef?.current?.scrollIntoView();
  };

  const handleSortAZ = () => {
    let sortObj = cloneDeep(store.recipeFilters);
    sortObj.sortAsc = true;
    sortObj.sortByCreatedDate = false;
    store.setRecipeFilters(sortObj);
    store.setRecipeSelectedPage(1);
    handleCloseSortMenu();
  };

  const handleSortZA = () => {
    let sortObj = cloneDeep(store.recipeFilters);
    sortObj.sortAsc = false;
    sortObj.sortByCreatedDate = false;
    store.setRecipeFilters(sortObj);
    store.setRecipeSelectedPage(1);
    handleCloseSortMenu();
  };

  // the api orders by CreatedDate itself, so sortAsc true is the oldest record first
  const handleSortOldestNewest = () => {
    let sortObj = cloneDeep(store.recipeFilters);
    sortObj.sortAsc = true;
    sortObj.sortByCreatedDate = true;
    store.setRecipeFilters(sortObj);
    store.setRecipeSelectedPage(1);
    handleCloseSortMenu();
  };

  const handleSortNewestOldest = () => {
    let sortObj = cloneDeep(store.recipeFilters);
    sortObj.sortAsc = false;
    sortObj.sortByCreatedDate = true;
    store.setRecipeFilters(sortObj);
    store.setRecipeSelectedPage(1);
    handleCloseSortMenu();
  };

  const handleCloseSortMenu = () => {
    setSortMenuAnchor(null);
  };

  const handleOpenSortMenu = (e) => {
    setSortMenuAnchor(e.currentTarget);
  };

  const handleToggleShowInactive = () => {
    let sortObj = cloneDeep(store.recipeFilters);
    sortObj.showInactive = !sortObj.showInactive;
    store.setRecipeFilters(sortObj);
    store.setRecipeSelectedPage(1);
    localStorage.setItem("showInactiveRecipes", sortObj.showInactive);
  };

  const handleSearch = (e) => {
    if (!e.target.value) {
      queryClient.invalidateQueries({
        queryKey: ["recipes-paginated"],
      });
      queryClient.refetchQueries({
        queryKey: ["recipes-paginated"],
        type: "all",
      });
    }
    store.setRecipeSearchText(e.target.value);
  };

  const debouncedSearchHandler = debounce(handleSearch, 300);

  const handleSearchChange = (e) => {
    setLocalSearchText(e.target.value);
    debouncedSearchHandler.cancel();
    store.setRecipeSelectedPage(1);
    debouncedSearchHandler(e);
  };

  const handleSearchTypeChange = (newSearchType) => {
    store.setRecipeSearchType(newSearchType);
    store.setRecipeSelectedPage(1);
  };

  const handleAddRecipe = (payload) => {
    payload.establishmentId = establishmentid;
    addRecipeMutation.mutate(payload);
  };

  const toggleTagsModal = () => {
    toggleTagsModalOpen(!tagsModalOpen);
  };

  const handleTagClick = (tag) => {
    let sortObj = cloneDeep(store.recipeFilters);
    if (sortObj.tagFilters.find((x) => x === tag.id)) {
      sortObj.tagFilters = sortObj.tagFilters.filter((x) => x !== tag.id);
      store.setRecipeFilters(sortObj);
      store.setRecipeSelectedPage(1);
    } else {
      sortObj.tagFilters.push(tag.id);
      store.setRecipeFilters(sortObj);
      store.setRecipeSelectedPage(1);
    }
  };

  const handleSaveTagFilterClick = (tagFilterObj) => {
    let sortObj = cloneDeep(store.recipeFilters);
    sortObj.tagFilters = tagFilterObj;
    store.setRecipeFilters(sortObj);
    store.setRecipeSelectedPage(1);
  };

  const handleUpgradeModalToggle = () => {
    setUpgradeAccountModalOpen(!upgradeAccountModalOpen);
  };

  const handleCreateCheckout = async (interval) => {
    toggleLoadingSession(true);
    await createStripeCheckoutSession(establishmentid, interval);
    toggleLoadingSession(false);
  };

  useEffect(() => {
    const activeTags = recipeTagData?.tags?.filter((tag) =>
      store.recipeFilters.tagFilters.includes(tag.id)
    );
    setActiveTagFilters(activeTags);
  }, [store.recipeFilters.tagFilters, recipeTagData]);

  if (
    (isLoadingPaginatedRecipes && recipeTagData?.recipes === undefined) ||
    (isLoadingTags && recipeTagData?.tags === undefined) ||
    isLoadingSubscription ||
    isLoadingRateLimits ||
    isLoadingFullLibrary ||
    isLoadingMemberships
  ) {
    return <SpecSpinner open text={"Loading Recipes..."} />;
  }

  const recipeRateLimits = rateLimitData?.sectionLimits?.find(
    (x) => x.type === inventoryObjects.rateLimitType.recipes
  );
  const isRateLimited = subscriptionData?.isActive
    ? false
    : rateLimitData?.ignoreLimits || recipeRateLimits?.isUnderLimit
    ? false
    : true;

  const isEstablishmentOwner =
    memberships?.establishmentMemberships?.find(
      (x) => x.establishmentId === establishmentid
    )?.isOwner === true
      ? true
      : false;
  return (
    <>
      <Box
        sx={{
          width: "100%",
          maxWidth: "2440px",
          padding: 4,
          marginTop: 6,
        }}
      >
        {subscriptionData?.isActive === false ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              padding: 2,
              marginBottom: 4,
              height: "72px",
              borderRadius: 1,
              background: "linear-gradient(90deg, #C74822 0%, #7C1D00 100%)",
            }}
          >
            <Grid
              container
              direction="row"
              sx={{ display: "flex", alignItems: "center" }}
            >
              <Grid
                item
                xs={10}
                sx={{ display: "flex", flexDirection: "column" }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: { xs: 10, sm: 12, md: 16 },
                  }}
                >
                  You are on a Spec Free plan
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: "#fff",
                    fontSize: { xs: 8, sm: 10, md: 12 },
                    marginTop: 1,
                  }}
                >
                  The Spec free plan includes up to 30 free recipes! Upgrade to
                  Spec Pro for unlimited recipes, menus, invoice tracking,
                  reporting and more!
                </Typography>
              </Grid>
              <Grid
                item
                xs={2}
                sx={{
                  display: "flex",
                  alignItems: { xs: "flex-start", md: "center" },
                  justifyContent: "flex-end",
                }}
              >
                <Button
                  variant="text"
                  rel=" noreferrer"
                  onClick={() => handleUpgradeModalToggle()}
                >
                  <Typography
                    variant="smallButton"
                    sx={{ color: "#fff", fontSize: { xs: 8, md: 12 } }}
                  >
                    Upgrade
                  </Typography>
                </Button>
              </Grid>
            </Grid>
          </Box>
        ) : null}
        <Box sx={{ marginBottom: 6 }}>
          <Grid
            container
            sx={{ display: "flex", justifyContent: "space-between" }}
          >
            <Grid item xs={9}>
              <Typography variant="h2">All Recipes</Typography>
            </Grid>
            <Grid
              item
              xs={3}
              sx={{ display: "flex", justifyContent: "flex-end" }}
            >
              <Button
                variant="contained"
                disabled={isRateLimited}
                onClick={() => toggleCreatingRecipeModalOpen(true)}
              >
                <AddOutlinedIcon />
                <Typography
                  variant="largeButton"
                  sx={{
                    fontSize: { xs: 10, md: 16 },
                    lineHeight: { xs: 1, md: 1.5 },
                  }}
                >
                  Create New Recipe
                </Typography>
              </Button>
            </Grid>
          </Grid>
        </Box>
        <Grid
          container
          direction="row"
          sx={{
            display: "flex",
            alignItems: "center",
          }}
        >
          <Grid item xs={12} md={6}>
            {/* SEARCH BAR */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 1,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                }}
              >
                <Box sx={{ marginBottom: 3 }}>
                  <SearchByToggle
                    searchType={store?.recipeSearchType}
                    handleSearchTypeChange={handleSearchTypeChange}
                  />
                </Box>
                <TextField
                  variant="outlined"
                  size="small"
                  fullWidth
                  autoComplete="off"
                  id="recipeSearch"
                  onChange={handleSearchChange}
                  value={localSearchText}
                  inputProps={{
                    sx: {
                      marginBottom: 1,
                    },
                  }}
                  sx={{
                    backgroundColor: (theme) => theme.palette.pure.white,
                    borderColor: (theme) => theme.palette.terrain[400],
                    width: "340px",
                    borderRadius: 1,
                  }}
                  label={
                    <>
                      <SearchOutlinedIcon style={{ paddingRight: "5px" }} />
                      {isIngredientSearch
                        ? "Search by ingredient name"
                        : "Search by recipe name"}
                    </>
                  }
                />
              </Box>
            </Box>
          </Grid>
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: { xs: "space-between", md: "flex-end" },
              marginTop: { xs: 4, md: 0 },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                padding: 0.75,
              }}
            >
              <Switch
                checked={store?.recipeFilters.showInactive}
                onChange={handleToggleShowInactive}
                size="small"
              />
              <Typography variant="body1" sx={{ fontSize: 12, marginLeft: 1 }}>
                Show Inactive
              </Typography>
            </Box>
            <Box>
              <Button
                variant="outlined"
                sx={{ marginRight: 1, marginLeft: 2 }}
                onClick={() => toggleTagsModalOpen(true)}
              >
                <Typography variant="largeButton">Tags</Typography>
              </Button>
              <Button
                variant="outlined"
                sx={{ marginRight: 1, marginLeft: 2 }}
                onClick={handleOpenSortMenu}
              >
                <FilterListIcon sx={{ fontSize: 18, marginRight: 2 }} />
                <Typography variant="largeButton">Sort</Typography>
              </Button>
              <Menu
                anchorEl={sortMenuAnchor}
                open={!!sortMenuAnchor}
                onClose={handleCloseSortMenu}
              >
                <MenuItem
                  sx={{ margin: "5px" }}
                  selected={
                    store?.recipeFilters.sortAsc &&
                    !store?.recipeFilters.sortByCreatedDate
                  }
                  id="name,asc"
                  onClick={handleSortAZ}
                >
                  Name A-Z
                </MenuItem>
                <MenuItem
                  sx={{ margin: "5px" }}
                  id="name,desc"
                  selected={
                    !store?.recipeFilters.sortAsc &&
                    !store?.recipeFilters.sortByCreatedDate
                  }
                  onClick={handleSortZA}
                >
                  Name Z-A
                </MenuItem>
                <MenuItem
                  sx={{ margin: "5px" }}
                  id="createdDate,desc"
                  selected={
                    !store?.recipeFilters.sortAsc &&
                    store?.recipeFilters.sortByCreatedDate
                  }
                  onClick={handleSortNewestOldest}
                >
                  Newest First
                </MenuItem>
                <MenuItem
                  sx={{ margin: "5px" }}
                  id="createdDate,asc"
                  selected={
                    store?.recipeFilters.sortAsc &&
                    store?.recipeFilters.sortByCreatedDate
                  }
                  onClick={handleSortOldestNewest}
                >
                  Oldest First
                </MenuItem>
              </Menu>
            </Box>
          </Grid>
        </Grid>
        <Box sx={{ marginTop: 4 }}>
          <Grid container>
            <Grid
              item
              sx={{
                display: "flex",
                flexDirection: "row",
                width: { xs: "300px", sm: "100%" },
                maxWidth: "100%",
                flexWrap: "wrap",
              }}
            >
              <Grid
                direction="row"
                container
                flexWrap={2}
                sx={{ maxWidth: "100%" }}
              >
                {activeTagFilters?.map((x) => (
                  <TagPill
                    key={x.id}
                    onClickCallback={handleTagClick}
                    selected={true}
                    tag={x}
                  />
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Box>

        {isLoadingPaginatedSearchData ? (
          <Box
            sx={{
              display: "flex",
              width: "100%",
              height: "100%",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <CircularProgress />
          </Box>
        ) : store?.recipeSearchText ? (
          <>
            <RecipeList
              recipes={paginatedSearchData?.recipes}
              recipeTags={recipeTagData.tags}
              isLoadingPaginatedSearchData={isLoadingPaginatedSearchData}
            />
          </>
        ) : !store?.recipeSearchText &&
          (paginatedRecipeData?.recipes.length > 0 ||
            store?.recipeFilters?.tagFilters?.length > 0) ? (
          <RecipeList
            recipes={paginatedRecipeData?.recipes}
            recipeTags={recipeTagData.tags}
            isLoadingPaginatedRecipes={isLoadingPaginatedRecipes}
          />
        ) : (
          <CreateRecipeGuide toggleCreatingRecipeModalOpen={toggleCreatingRecipeModalOpen} />
        )}
        <CreateRecipeModal
          isOpen={creatingRecipeModalOpen}
          toggleModalOpen={toggleCreatingRecipeModalOpen}
          headerText="Create New Recipe"
          buttonText="Create Recipe"
          loading={addRecipeMutation.isLoading}
          addRecipe={handleAddRecipe}
        />
        <TagsModal
          isOpen={tagsModalOpen}
          toggleModalOpen={toggleTagsModal}
          recipeTags={recipeTagData}
          isLoadingTags={isLoadingTags}
          tagFilters={store.recipeFilters.tagFilters}
          saveTagFilters={handleSaveTagFilterClick}
          handleCreateTagCallback={handleCreateTagCallback}
        />
        <UpgradeAccountModal
          isOpen={upgradeAccountModalOpen}
          handleModalToggle={handleUpgradeModalToggle}
          isLoadingSession={isLoadingSession}
          isOwner={isEstablishmentOwner}
          upgradeCallback={handleCreateCheckout}
        />
        <Pagination
          sx={{ marginTop: 6 }}
          count={
            store?.recipeSearchText
              ? paginatedSearchData?.totalPages
              : paginatedRecipeData?.totalPages
          }
          onChange={handlePageSet}
          page={store?.recipeSelectedPage}
        />
      </Box>
    </>
  );
};

export default Recipes;
